/**
 * Máscaras e validações client-side do wizard de auto-cadastro.
 * Nota: validações aqui são apenas para UX — a autoridade é sempre o backend.
 */
import i18n from '../../../i18n';

/** Remove tudo que não é dígito. */
export function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/** Formata CEP como 00000-000. */
export function maskZipCode(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/** Formata telefone celular BR como (00) 00000-0000. */
export function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/** CEP válido = 8 dígitos. */
export function isValidZipCode(value: string): boolean {
  return onlyDigits(value).length === 8;
}

/** Celular válido = 10 ou 11 dígitos. */
export function isValidPhone(value: string): boolean {
  const len = onlyDigits(value).length;
  return len === 10 || len === 11;
}

/** Validação de e-mail suficiente para UX (backend é a autoridade). */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export interface PasswordStrength {
  /** 0 (vazio) a 4 (forte). */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** Cor Tailwind (arbitrary value) para a barra. */
  color: string;
}

/**
 * Avalia a força da senha combinando comprimento e variedade de caracteres.
 * Mínimo recomendado: 8 caracteres.
 */
export function evaluatePassword(password: string): PasswordStrength {
  if (!password) return { score: 0, label: '', color: '#BAD4E5' };

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score += 1;

  const clamped = Math.min(score, 4) as PasswordStrength['score'];
  const map: Record<number, { label: string; color: string }> = {
    0: { label: 'Muito fraca', color: '#e84b3d' },
    1: { label: 'Fraca', color: '#e84b3d' },
    2: { label: 'Razoável', color: '#f1c40e' },
    3: { label: 'Boa', color: '#00B1FF' },
    4: { label: 'Forte', color: '#99CC29' },
  };
  return { score: clamped, ...map[clamped] };
}

/** Requisito mínimo para envio: 8+ caracteres. */
export function isAcceptablePassword(password: string): boolean {
  return password.length >= 8;
}

/** Extensões e tamanho aceitos para o documento comprobatório. */
export const ACCEPTED_DOC_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const MAX_DOC_SIZE_BYTES = 8 * 1024 * 1024; // 8 MB

export interface FileValidationResult {
  ok: boolean;
  error?: string;
}

export function validateDocumentFile(file: File): FileValidationResult {
  if (!ACCEPTED_DOC_TYPES.includes(file.type)) {
    return { ok: false, error: i18n.t('views.registration.document.invalid_format', 'Formato inválido. Envie JPG, PNG, WEBP ou PDF.') };
  }
  if (file.size > MAX_DOC_SIZE_BYTES) {
    return { ok: false, error: i18n.t('views.registration.document.too_large', 'Arquivo muito grande. O limite é de 8 MB.') };
  }
  return { ok: true };
}

/** Formata bytes de forma legível. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
